import { TimeFrequency } from '../../../../../common/js/static/Product';
import { CoverpathUtils, ReasonForOR1 } from '../../../../js/static/CoverpathUtils';
import { Utils } from '../../../../../common/js/Utils';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { MMDirectPolicy } from '../../../../../coverpath-common/classes/mmdirect_subclasses/MMDirectPolicy';
import { CoverpathPolicy } from '../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import moment from 'moment';
import { Exchange1035FundingPurpose } from '../../../../js/interfaces/CoverpathTypes';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { PolicyData } from '../../../interfaces/PolicyData';
import { CasApplication } from '../../../../classes/coverpath_subclasses/CasApplication';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

type PolicyStatusText = {
  title?: string[];
  subtitle?: string[];
};

export abstract class BasePolicyStatus {
  controller;
  accountCenterService;
  policy: CoverpathPolicy | MMDirectPolicy;
  primaryPolicyData: PolicyData;
  secondaryPolicyData: PolicyData;
  advisorSignedApp: boolean = false;
  isJointApp: boolean = false;
  faceExceedsMaxTLICCoverage: boolean = false;
  insurabilitySignatureIsRequired: boolean = false;
  isTLIC: boolean = false;
  formattedPrice: string;
  formattedFace: string;
  timeFrequency: string;
  formattedMaxTLICCoverage: string;
  customerSignedApp: boolean;
  isOwnerSameAsInsured: boolean;
  singleTempFlatExtra: any;
  formattedOfferRequiredBy: string;
  tempFlatExtraSum: string;
  isPolicyTableRated: boolean;
  formattedFreeLookEndsOn: string;
  insuredFullName: string;
  insuredFirstName: string;
  maxTLICCoverage: number;
  formattedParamedRequiredBy: string;
  contactNumber: string;
  workflowAction: string;
  advisorDescriptiveNoun: string;
  isTermConversionPolicy: boolean;
  isWholeConversionPolicy: boolean;
  hasAgent: boolean;
  isFrozen: boolean;
  insuredEsignRequired: boolean = false;
  ownerCompletedDocusign: boolean = false;
  isLiteTouch: boolean = false;
  isAccelerated: boolean = false;
  insuredIsJuvenile: boolean;
  isLifeBridge: boolean;
  requiresSpecialHandling: boolean;
  isLiteTouchApproved: boolean;
  waitingOnOtherSigners: boolean;
  esignMetadata;
  signatureNeeded: boolean;
  isBlockedByAdvisor: boolean;
  waitingOnInsuredToSign: boolean;
  waitingOnSecondOwnerToSign: boolean;
  insuredSignerEmail: string;
  insuredEmail: string;
  otherOwnerEmail: string;
  insuredSignerFullName: string;
  insuredSignerFirstName: string;
  formattedNextPaymentDateDDB: string;
  paymentAuthorizationNeeded: boolean;
  nextPaymentDisplayDDBPast: boolean;
  hasPaymentFailed: boolean;
  formattedPaymentFailureDate: string;
  failedPaymentAmount: string;

  protected constructor(
    protected amorphicService: AmorphicService,
    policy: CoverpathPolicy | MMDirectPolicy,
    accountCenterService?: AccountCenterService
  ) {
    this.controller = amorphicService.controller;
    this.policy = policy;
    this.accountCenterService = accountCenterService;

    //Adding this to minimize impact to Annuities
    if (!policy.isAnnuity()) {
      this.setAllProperties();
    }
  }

  setAllProperties(): void {
    if (!this.policy.isAnnuity()) {
      this.primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(
        this.policy.application.primaryPolicyWithPendingAmendment.__id__
      );
      if (this.policy.application.isJointAppWithPendingAmendment()) {
        this.secondaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(
          this.policy.application.secondaryPolicyWithPendingAmendment.__id__
        );
      }
    }

    const primaryCACPaymentData = this.primaryPolicyData.getCACPaymentData();
    let secondaryCACPaymentData;
    if (this.secondaryPolicyData) {
      secondaryCACPaymentData = this.secondaryPolicyData.getCACPaymentData();
    }
    this.advisorDescriptiveNoun = CoverpathUtils.getAdvisorDescriptiveNoun();

    this.timeFrequency = TimeFrequency[this.primaryPolicyData.getCACPaymentData().getCACPaymentFrequency()];
    this.isPolicyTableRated = this.primaryPolicyData.getCACHasTableRating();
    this.insurabilitySignatureIsRequired = CoverpathUtils.isInsurabilityStatementSignatureRequired(this.policy);
    this.insuredFullName = this.primaryPolicyData.getCACInsuredFullName();
    this.insuredFirstName = this.primaryPolicyData.getCACInsuredFirstName();
    this.isOwnerSameAsInsured = this.primaryPolicyData.getCACIsOwnerSameAsInsured();
    this.isTLIC = this.primaryPolicyData.getCACIsTLIC();
    this.formattedOfferRequiredBy = this.primaryPolicyData.getCACOfferRequiredBy()
      ? this.primaryPolicyData.getCACOfferRequiredBy().toLocaleDateString()
      : '';
    this.formattedParamedRequiredBy = this.primaryPolicyData.getCACParamedRequiredBy()
      ? this.primaryPolicyData.getCACParamedRequiredBy().toLocaleDateString()
      : '';
    this.formattedFreeLookEndsOn = this.primaryPolicyData.getCACFreeLookEndsOn()
      ? this.primaryPolicyData.getCACFreeLookEndsOn().toLocaleDateString()
      : '';
    this.customerSignedApp = this.primaryPolicyData.getCACApplicationSignedDate() !== null;
    this.formattedNextPaymentDateDDB = primaryCACPaymentData.getCACNextPaymentDisplayDDB()
      ? Utils.formatMonthWithOrdinalDay(primaryCACPaymentData.getCACNextPaymentDisplayDDB())
      : '';

    this.ownerCompletedDocusign =
      this.primaryPolicyData.getCACOwnerCompletedDocusign() && this.controller.isDocuSignRedirectEnabled;

    this.formattedFace = Utils.formatCurrency(
      primaryCACPaymentData.getCACFaceAmount() +
        (secondaryCACPaymentData ? secondaryCACPaymentData.getCACFaceAmount() : 0)
    );
    this.formattedPrice = Utils.formatCurrency(
      primaryCACPaymentData.getCACTotalPremium() +
        (secondaryCACPaymentData ? secondaryCACPaymentData.getCACTotalPremium() : 0),
      2
    );
    this.maxTLICCoverage = this.primaryPolicyData.getCACMaxTLICCoverage();
    this.formattedMaxTLICCoverage = this.primaryPolicyData.getCACFormattedMaxTLICCoverage();
    this.faceExceedsMaxTLICCoverage = this.primaryPolicyData.getCACFaceExceedsMaxTLICCoverage();
    this.singleTempFlatExtra = primaryCACPaymentData.getCACSingleTempFlatExtra();
    this.tempFlatExtraSum = primaryCACPaymentData.getCACTempFlatExtraSum()
      ? Utils.formatCurrency(
          primaryCACPaymentData.getCACTempFlatExtraSum() +
            (secondaryCACPaymentData ? secondaryCACPaymentData.getCACTempFlatExtraSum() : 0)
        )
      : null;
    this.workflowAction = `${this.primaryPolicyData.getCACWorkflowState()}:${this.primaryPolicyData.getCACWorkflowSubState()}`;
    this.isTermConversionPolicy = !!this.primaryPolicyData.getCACConvertedTo();
    this.isWholeConversionPolicy = !!this.primaryPolicyData.getCACConvertedFrom();
    this.isFrozen = this.primaryPolicyData.getCACIsFrozen();
    this.hasAgent = this.primaryPolicyData.getCACHasAgent();
    this.isAccelerated = this.primaryPolicyData.getCACIsAccelerated();
    this.isLiteTouch = this.primaryPolicyData.getCACIsLiteTouch();
    this.isJointApp = this.primaryPolicyData.getCACIsJointApp();
    this.advisorSignedApp = this.primaryPolicyData.getCACIsAdvisorSigned();
    this.insuredIsJuvenile = this.primaryPolicyData.getCACInsuredIsJuvenile();
    this.isLifeBridge = this.primaryPolicyData.getCACIsLifeBridge();
    this.isLiteTouchApproved = this.primaryPolicyData.getCACIsLiteTouchApproved();
    this.requiresSpecialHandling = this.primaryPolicyData.getCACRequiresSpecialHandling();
    this.waitingOnOtherSigners = this.primaryPolicyData.getCACWaitingOnOtherSigners();
    this.esignMetadata = this.primaryPolicyData.getCACESignMetaData();
    this.signatureNeeded = this.primaryPolicyData.getCACSignatureNeeded();
    this.isBlockedByAdvisor = this.primaryPolicyData.getCACIsBlockedByAdvisor();
    this.waitingOnInsuredToSign = this.primaryPolicyData.getCACWaitingOnInsuredToSign();
    this.waitingOnSecondOwnerToSign = this.primaryPolicyData.getCACWaitingOnSecondOwnerToSign();
    this.insuredFullName = this.primaryPolicyData.getCACInsuredFullName();
    this.insuredFirstName = this.primaryPolicyData.getCACInsuredFirstName();
    this.insuredSignerEmail = this.primaryPolicyData.getCACInsuredSignerEmail();
    this.insuredSignerFullName = this.primaryPolicyData.getCACInsuredSignerFullName();
    this.insuredSignerFirstName = this.primaryPolicyData.getCACInsuredSignerFirstName();
    this.insuredEmail = this.primaryPolicyData.getCACInsuredEmail();
    this.otherOwnerEmail = this.primaryPolicyData.getCACOtherOwnerEmail();
    this.paymentAuthorizationNeeded =
      this.primaryPolicyData.getCACPaymentData().getCACNeedsAuthorizationWithinOrPast() ||
      this.secondaryPolicyData?.getCACPaymentData()?.getCACNeedsAuthorizationWithinOrPast();
    this.nextPaymentDisplayDDBPast =
      this.primaryPolicyData.getCACPaymentData().getCACNextPaymentDisplayDDBPast() ||
      this.secondaryPolicyData?.getCACPaymentData()?.getCACNextPaymentDisplayDDBPast();
    this.hasPaymentFailed = this.isSecondaryPaymentFailed()
      ? secondaryCACPaymentData.getCACHasPaymentFailed()
      : primaryCACPaymentData.getCACHasPaymentFailed();
    this.formattedPaymentFailureDate = this.isSecondaryPaymentFailed()
      ? Utils.formatMonthWithOrdinalDay(secondaryCACPaymentData.getCACPaymentFailueDate())
      : Utils.formatMonthWithOrdinalDay(primaryCACPaymentData.getCACPaymentFailueDate());
    this.failedPaymentAmount = this.isSecondaryPaymentFailed()
      ? Utils.formatCurrency(secondaryCACPaymentData.getCACFailedPaymentAmount())
      : Utils.formatCurrency(primaryCACPaymentData.getCACFailedPaymentAmount());
  }

  isSecondaryPaymentFailed(): boolean {
    return (
      this.secondaryPolicyData &&
      this.secondaryPolicyData.getCACPaymentData() &&
      this.secondaryPolicyData.getCACPaymentData().getCACHasPaymentFailed() &&
      !this.primaryPolicyData.getCACPaymentData().getCACHasPaymentFailed()
    );
  }

  makePluralIfJA(input: string): string {
    if (!this.isJointApp) {
      return input;
    }

    switch (input) {
      case 'policy':
        return 'policies';
      case 'policy is':
        return 'policies are';
      case 'policy has':
        return 'policies have';
      default:
        return input;
    }
  }

  getPolicyStatusTitle(): string[] {
    switch (this.workflowAction) {
      case 'Accelerated Application:Waiting for Acceptance':
        return [
          `Congratulations!`,
          `You qualify for our express path process.`,
          `Your final premium is ${this.formattedPrice}/${this.timeFrequency}.`,
        ];
      case 'TLIC Issued:Waiting for Policy Acceptance':
      case 'Application:Waiting for Issue Acceptance':
        if (this.insuredEsignRequired) {
          return [`Congratulations, your policy has been approved.`];
        }

        const result: string[] = [`Your ${this.makePluralIfJA('policy is')} ready to go.`];
        if (!this.isLifeBridge) {
          result.push(`Your final premium is ${this.formattedPrice}/${this.timeFrequency}.`);
        }
        return result;
      case 'TLIC Application:Waiting for Acceptance':
        if (this.isOwnerSameAsInsured) {
          return [`Application received. You qualify for temporary coverage.`];
        } else {
          return [`Application received. ${this.insuredFirstName} qualifies for temporary coverage.`];
        }
      case 'TLIC Application:Determine Underwriting Class':
        if (this.isAccelerated && this.isFrozen) {
          return [`Your application is on hold`];
        }
        return [`We are working on your application.`];
      case 'Haven Application:Waiting for Application Acceptance':
        return [`You're on your way to getting covered.`];
      case 'Accelerated Application:Waiting for Signature': {
        const result: string[] = [];
        const esignMetadata = this.esignMetadata;
        if (esignMetadata && esignMetadata.ownerSignComplete && esignMetadata.waitingOnOtherSigners) {
          // this order is important we show the insured message first even when there are two more parties
          if (esignMetadata.waitingOnInsured) {
            return [`Next step: we need the insured's signature`];
          } else if (esignMetadata.waitingOnSecondOwner) {
            return [`Next step: we need the joint owner's signature`];
          }
        } else if ((this.customerSignedApp || this.ownerCompletedDocusign) && !this.advisorSignedApp && this.hasAgent) {
          result.push(`Congratulations, your ${this.makePluralIfJA('policy has')} been approved.`);
        } else if (!this.customerSignedApp && !this.ownerCompletedDocusign) {
          if (this.controller.isDocuSignRedirectEnabled) {
            result.push('Next step: we need your signature.');
          } else {
            result.push('Next step: check your email.');
          }
        }
        return result;
      }
      case 'TLIC Application:Waiting for Signature':
      case 'Haven Application:Waiting for Signature': {
        const esignData = this.esignMetadata;
        const isBlockedByAdvisor =
          (this.customerSignedApp || this.ownerCompletedDocusign) &&
          !this.advisorSignedApp &&
          this.hasAgent &&
          this.requiresSpecialHandling;
        if (esignData && esignData.ownerSignComplete && esignData.waitingOnOtherSigners) {
          if (esignData.waitingOnInsured) {
            return [`Next step: we need the insured's signature`];
          } else if (esignData.waitingOnSecondOwner) {
            return [`Next step: we need the joint owner's signature`];
          }
        } else if (isBlockedByAdvisor) {
          return [`Next step: awaiting your ${this.advisorDescriptiveNoun}'s signature.`];
        } else if (esignData && esignData.ownerSignComplete) {
          return ['Thank you for eSigning your documents.'];
        } else if (!this.controller.isDocuSignRedirectEnabled) {
          return [`Next step: check your email.`];
        } else {
          return [`Next step: we need your signature.`];
        }
      }
      case 'Haven Application:Waiting for Advisor Signature':
        return [`Next step: awaiting your ${this.advisorDescriptiveNoun}'s signature.`];
      case 'Haven Application:Waiting for Labs':
      case 'TLIC Issued:Waiting for Labs':
        if (this.insuredIsJuvenile || this.isLiteTouch) {
          return [`We are working on your application.`];
        }
        return [`Next step: we need your medical exam results.`];
      case 'TLIC Issued:Final Underwriting':
      case 'Manual Underwriting:Final Review':
      case 'Application:Final Review':
      case 'Manual Underwriting:Awaiting APS':
      case 'Advisor Amendment:Waiting for Advisor Amendment':
      case 'Underwriting:Underwriting':
      case 'Advisor Decline Review:Waiting for Advisor':
        //since we can be in UW2 before app sign, we should ignore that stage
        if (!this.customerSignedApp) {
          if (this.isAccelerated) {
            return [`Congratulations!`, `You qualify for our express path process.`];
          }
          return [];
        } else if (this.isFrozen) {
          return [`Your application is on hold`];
        } else if (this.insuredIsJuvenile) {
          return [`We are working on your application.`];
        } else if (this.isLiteTouch) {
          if (this.isLiteTouchApproved) {
            return [`Congratulations!`, `You qualify for our express path process.`];
          }
          return [`We are working on your application.`];
        }
        return [`Thank you for completing your medical exam.`];
      case 'Application:Issuing Policy':
      case 'TLIC Issued:Issuing Policy':
      case 'Accelerated Application:Issuing Policy':
        return [`Congratulations, you're covered!`];
      case 'In Force:Pending Policy Docs':
      case 'In Force:Free Look Period':
      case 'In Force:Grace Period':
      case 'In Force:Issued':
        if (this.paymentAuthorizationNeeded) {
          return [
            `Your ${!this.isJointApp ? this.formattedPrice : ''} premium ${
              this.nextPaymentDisplayDDBPast ? 'was' : 'is'
            } due on ${this.formattedNextPaymentDateDDB}.`,
          ];
        }
        if (this.isTermConversionPolicy) {
          return ['Your Whole Life policy conversion is pending.'];
        }
        if (this.hasPaymentFailed) {
          return [`Your ${this.failedPaymentAmount} premium payment failed on ${this.formattedPaymentFailureDate}.`];
        }
        return [`Congratulations, you're covered!`];
      case 'Waiting for Agent Signature':
        return [`Congratulations, your ${this.makePluralIfJA('policy has')} been approved.`];
      case 'Operations Review:Operations Review':
        const reason = CoverpathUtils.reasonForOR1(this.policy);

        if (reason === ReasonForOR1.CP_CASE) {
          return [`Congratulations, your policy has been approved.`];
        } else {
          return [`We need more time to review your application`];
        }
      case 'Funding Pause:Initiate Funds Transfer':
        return [`Your ${this.makePluralIfJA('policy is')} approved.`];
      case 'Funding Pause:Waiting for 1035 Funds':
      case 'Funding Pause:Funds have arrived':
      case 'Funding Pause:Process changes':
        return [`Your ${this.makePluralIfJA('policy is')} almost ready.`];
      case 'Conversion started:Conversion started':
        return [`Your conversion application is ready.`];
      case 'Conversion is Ready:Conversion is Ready':
        return [`Your conversion is almost complete.`];
      default:
        const error = {
          isHumanRelated: false,
          data: {
            workflowAction: this.workflowAction,
          },
        };
        UILoggerSingleton.getInstance().error({
          module: 'BasePolicyStatus',
          function: 'getPolicyStatusTitle',
          category: UICategory.request,
          error: error,
        });
        return [];
    }
  }

  async getPolicyStatusSubTitle(): Promise<string[]> {
    switch (this.workflowAction) {
      case 'Accelerated Application:Waiting for Acceptance':
        return [
          `With express path, you get the same pricing without the need for a medical exam. Your final premium is based on your application answers and the data we collect.`,
          `eSign your documents to start your coverage right away.`,
          `*Please note that your coverage will only begin if payment is received.`,
        ];

      case 'TLIC Issued:Waiting for Policy Acceptance':
      case 'Application:Waiting for Issue Acceptance': {
        const result: string[] = [];
        if (this.insuredEsignRequired) {
          result.push(
            `We've emailed ${this.insuredSignerFullName} (${this.insuredSignerEmail}) asking for their signature. Once ${this.insuredSignerFirstName} eSigns, your policy will be issued.`
          );
        } else {
          if (
            this.policy.hasAlirRiderWithPendingAmendment() &&
            this.policy.alirRider.hasAlir1035ExchangeWithPendingAmendment() &&
            this.policy.alirRider.alir1035ApplyTowardsWithPendingAmendment() ===
              Exchange1035FundingPurpose.PREMIUM_AND_ALIR
          ) {
            result.push(`You have until ${this.formattedOfferRequiredBy} to accept your offer.`);
            if (
              this.policy.alirRider.alir1035ExchangeAmountWithPendingAmendment() >=
              this.policy.application.primaryPolicyWithPendingAmendment.premiumBreakdown.getTotalWithPendingAmendment()
            ) {
              if (this.isJointApp) {
                result.push(`Once you accept, your whole life policy will be in force. We will draft your initial term policy premium from
                 your bank to start your coverage${
                   this.isTLIC ? ` and refund what you paid for temporary coverage` : ``
                 }. Your initial whole life
                  premium will be covered by 1035 exchange funds.`);
              } else {
                result.push(
                  `Once you accept, your whole life policy will be in force. ${
                    this.isTLIC
                      ? `We will refund what you paid for temporary
                 coverage.`
                      : ``
                  } Your initial whole life premium will be covered by 1035 exchange funds.`
                );
              }
            } else {
              result.push(`Once you accept, we will draft any difference between your 1035 exchange transfer amount and your final
              premium to start your coverage${
                !this.isTLIC ? `.` : ` and refund what you paid for temporary coverage.`
              }`);
            }
          } else {
            result.push(`Accept your offer now to activate your coverage.`);
            if (this.policy.paymentInformationGathered) {
              result.push(`To accept this offer, you’ll sign additional documents on the following page.`);
            } else {
              result.push(
                `To accept this offer, you’ll provide your payment information and sign additional documents on the following pages.`
              );
            }
            result.push(`This offer expires on ${this.formattedOfferRequiredBy}.`);
          }

          if (this.singleTempFlatExtra) {
            result.push(
              `Your policy has a temporary flat extra of ${this.singleTempFlatExtra.amount} that expires after ${this.singleTempFlatExtra.expiresFormatted}.`
            );
          }

          if (this.tempFlatExtraSum) {
            result.push(
              `Your policy has multiple temporary flat extras that total ${this.tempFlatExtraSum}. View your premium details below to see when they expire.`
            );
          }
        }

        return result;
      }
      // at this stage the workflowAction provided by the account center service
      // may have been modified by an alias call, so the policy can have
      // diffrent workflowState/workflowSubStates
      case 'TLIC Application:Waiting for Acceptance': {
        const tempCoverageTxt: string = 'Temporary coverage provides immediate protection for up to 120 days while';
        const policyIsInSignature =
          this.policy.futureWorkflowSubState === 'Waiting for Signature' ||
          this.policy.workflowSubState === 'Waiting for Signature';

        if (this.isLiteTouch) {
          const texts = [
            `${tempCoverageTxt} ${
              this.isOwnerSameAsInsured
                ? 'you await your final offer.'
                : `${this.insuredFirstName} awaits their final offer.`
            }`,
          ];
          if (!policyIsInSignature) {
            texts.push(`Next step: accept or decline temporary coverage.`);
          }
          return texts;
        } else {
          const texts = [
            `${tempCoverageTxt} ${
              this.isOwnerSameAsInsured
                ? 'you complete the medical exam and await your final offer.'
                : `${this.insuredFirstName} completes the medical exam and awaits their final offer.`
            }`,
          ];
          if (!policyIsInSignature) {
            texts.push(`Next step: accept or decline temporary coverage.`);
          }
          return texts;
        }
      }
      case 'TLIC Application:Determine Underwriting Class':
        if (this.isAccelerated && this.isFrozen) {
          return [
            `Since your application indicated you are replacing another policy ` +
              `with this one, our team needs to check a few details before we can ` +
              `proceed. This should take no more than a few days.`,
          ];
        }
      case 'Haven Application:Waiting for Application Acceptance':
        return [`We just need your eSignature to finish reviewing your application.`];
      case 'Accelerated Application:Waiting for Signature': {
        const result: string[] = [];
        const esignData = this.esignMetadata;
        if (esignData && esignData.ownerSignComplete && esignData.waitingOnOtherSigners) {
          let pronoun;
          let email;
          if (esignData.waitingOnInsured) {
            pronoun = 'insured';
            email = this.insuredEmail;
          } else if (esignData.waitingOnSecondOwner) {
            pronoun = 'joint owner';
            email = this.otherOwnerEmail;
          }
          return [
            `We've emailed the ${pronoun} (${email}) asking for their signature. Once the ${pronoun} signs, we can finish processing your application.`,
          ];
        } else if (!this.customerSignedApp && !this.ownerCompletedDocusign) {
          if (this.isOwnerSameAsInsured) {
            result.push(`We need you to eSign so your coverage can start immediately.`);
          } else {
            result.push(
              `We need you and the insured (or the insured's representative) to eSign so your coverage can start immediately.`
            );
          }
        } else if ((this.customerSignedApp || this.ownerCompletedDocusign) && !this.advisorSignedApp && this.hasAgent) {
          result.push(
            `Once your ${this.advisorDescriptiveNoun} eSigns, your ${this.makePluralIfJA('policy')} will be issued.`
          );
          result.push(`Please note that your coverage will only begin if payment is received.`);
        }

        return result;
      }

      case 'TLIC Application:Waiting for Signature': {
        const result: string[] = [];
        const esignData = this.esignMetadata;
        const isBlockedByAdvisor =
          (this.customerSignedApp || this.ownerCompletedDocusign) &&
          !this.advisorSignedApp &&
          this.hasAgent &&
          this.requiresSpecialHandling;

        if (esignData && esignData.ownerSignComplete && esignData.waitingOnOtherSigners) {
          let pronoun;
          let email;
          if (esignData.waitingOnInsured) {
            pronoun = 'insured';
            email = this.insuredEmail;
          } else if (esignData.waitingOnSecondOwner) {
            pronoun = 'joint owner';
            email = this.otherOwnerEmail;
          }
          return [
            `We've emailed the ${pronoun} (${email}) asking for their signature. Once the ${pronoun} signs, we can finish processing your application.`,
          ];
        } else if (isBlockedByAdvisor) {
          return [`We need your ${this.advisorDescriptiveNoun} to eSign so we can finish processing your application.`];
        } else if (esignData && esignData.ownerSignComplete) {
          return ['We are processing your signed packet.'];
        } else if (this.isOwnerSameAsInsured) {
          result.push('We need you to eSign so we can finish processing your application.');
          result.push('*Please note that your temporary coverage will only begin if payment is received.');
        } else {
          result.push(
            "We need you and the insured (or the insured's representative) to eSign so we can finish processing your application."
          );
          result.push('*Please note that your temporary coverage will only begin if payment is received.');
        }

        if (this.isTLIC && this.faceExceedsMaxTLICCoverage) {
          result.push(
            `While you have applied for ${this.formattedFace} in coverage, until your permanent policy has been approved and issued we can only provide you with up to ${this.formattedMaxTLICCoverage} in temporary coverage.`
          );
        }

        return result;
      }

      case 'Haven Application:Waiting for Signature': {
        const esignData = this.esignMetadata;
        const isBlockedByAdvisor =
          (this.customerSignedApp || this.ownerCompletedDocusign) &&
          !this.advisorSignedApp &&
          this.hasAgent &&
          this.requiresSpecialHandling;
        if (esignData && esignData.ownerSignComplete && esignData.waitingOnOtherSigners) {
          let pronoun;
          let email;
          if (esignData.waitingOnInsured) {
            pronoun = 'insured';
            email = this.insuredEmail;
          } else if (esignData.waitingOnSecondOwner) {
            pronoun = 'joint owner';
            email = this.otherOwnerEmail;
          }
          return [
            `We've emailed the ${pronoun} (${email}) asking for their signature. Once the ${pronoun} signs, we can finish processing your application.`,
          ];
        } else if (isBlockedByAdvisor) {
          return [`We need your ${this.advisorDescriptiveNoun} to eSign so we can finish processing your application.`];
        } else if (esignData && esignData.ownerSignComplete) {
          return ['Thank you for eSigning your documents.'];
        } else if (this.isOwnerSameAsInsured) {
          return [`We need you to eSign so we can finish processing your application.`];
        } else {
          return [
            `We need you and the insured (or the insured's representative) to eSign so we can finish processing your application.`,
          ];
        }
      }

      case 'Haven Application:Waiting for Advisor Signature': {
        return [`We need your ${this.advisorDescriptiveNoun} to eSign so we can finish processing your application.`];
      }

      case 'TLIC Issued:Waiting for Labs': {
        const result: string[] = [];
        if (!this.isLiteTouch) {
          result.push(
            `If you haven't completed your medical exam, please do so by ${this.formattedParamedRequiredBy} to avoid having your application canceled.`
          );
        }
        result.push(`*Please note that your temporary coverage will only begin if payment is received.`);
        if (this.isTLIC && this.faceExceedsMaxTLICCoverage) {
          result.push(
            `While you have applied for ${this.formattedFace} in coverage, until your permanent policy has been approved and issued we can only provide you with up to ${this.formattedMaxTLICCoverage} in temporary coverage.`
          );
        }

        return result;
      }

      case 'Haven Application:Waiting for Labs': {
        if (this.insuredIsJuvenile || this.isLiteTouch) {
          return [`Your decision will be ready soon.`];
        }
        return [
          `If you haven't completed your medical exam, please do so by ${this.formattedParamedRequiredBy} to avoid having your application cancelled.`,
        ];
      }

      case 'TLIC Issued:Final Underwriting':
      case 'Manual Underwriting:Final Review':
      case 'Application:Final Review':
      case 'Manual Underwriting:Awaiting APS':
      case 'Advisor Amendment:Waiting for Advisor Amendment':
      case 'Underwriting:Underwriting':
      case 'Advisor Decline Review:Waiting for Advisor': {
        const result: string[] = [];
        if (this.isFrozen && this.customerSignedApp) {
          result.push(
            `Since your application indicated you are replacing another policy ` +
              `with this one, our team needs to check a few details before we can ` +
              `proceed. This should take no more than a few days.`
          );
        } else if (this.isTLIC) {
          result.push('We are reviewing your results, and you will hear from us soon.');
          if (this.faceExceedsMaxTLICCoverage) {
            result.push(
              `While you have applied for ${this.formattedFace} in coverage, until your permanent policy has been approved and issued we can only provide you with up to ${this.formattedMaxTLICCoverage} in temporary coverage.`
            );
          }
        } else if (this.insuredIsJuvenile) {
          result.push(`Your decision will be ready soon.`);
        } else if (this.isAccelerated && this.hasAgent) {
          result.push(`With express path, you get the same pricing without the need for a medical exam.`);
          result.push(`Your ${this.advisorDescriptiveNoun} will contact you to discuss next steps.`);
        } else {
          result.push(`We are reviewing your results, and you will hear from us soon.`);
        }

        return result;
      }
      case 'Application:Issuing Policy':
      case 'TLIC Issued:Issuing Policy':
      case 'Accelerated Application:Issuing Policy':
        return [
          `Your ${this.makePluralIfJA(
            'policy is'
          )} active. We're preparing your policy documents and will email you as soon as they're ready. You can view your policy details below.`,
        ];
      case 'In Force:Pending Policy Docs':
        if (this.isTermConversionPolicy) {
          return ['Your current policy is still active. You can view your policy details below.'];
        }
        return [
          `Your ${this.makePluralIfJA(
            'policy is'
          )} active. We're preparing your policy documents and will email you as soon as they're ready. You can view your policy details below.`,
        ];
      case 'In Force:Free Look Period':
        if (this.isTermConversionPolicy) {
          return ['Your current policy is still active. You can view your policy details below.'];
        } else if (this.isLifeBridge) {
          return [];
        }
        if (this.hasPaymentFailed) {
          return ['You need to review your payment information and authorize the payment again.'];
        }
        return [
          `You may cancel your ${this.makePluralIfJA('policy')} without penalty before ${
            this.formattedFreeLookEndsOn
          } and any payments taken will be returned to you. You can view your policy details below.`,
        ];
      case 'In Force:Grace Period':
        if (this.paymentAuthorizationNeeded) {
          return ['You need to authorize your next premium payment.'];
        }
        if (this.isTermConversionPolicy) {
          return ['Your current policy is still active. You can view your policy details below.'];
        }
        const gracePeriodEndDate: Date = await this.controller.accountCenterController.getPAStatusEndGraceDate(
          this.policy.policyNumber
        );
        const gracePeriodEndDateString: string = gracePeriodEndDate.toLocaleDateString('en-US');
        return [
          `We have not yet received payment ${
            this.timeFrequency
          }ly premium. Your premium must be paid by ${gracePeriodEndDateString} or coverage will lapse and your ${this.makePluralIfJA(
            'policy'
          )} will be cancelled. For assistance, please contact MassMutual customer support at ${this.contactNumber}`,
        ];
      case 'In Force:Issued':
        if (this.paymentAuthorizationNeeded) {
          return ['You need to authorize your next premium payment.'];
        }
        if (this.isTermConversionPolicy) {
          return ['Your current policy is still active. You can view your policy details below.'];
        }
        if (this.hasPaymentFailed) {
          return ['You need to review your payment information and authorize the payment again.'];
        }

        return [`Your ${this.makePluralIfJA('policy is')} active. You can view your policy details below.`];
      case 'Waiting for Agent Signature':
        return [
          `Once your ${this.advisorDescriptiveNoun} eSigns, your ${this.makePluralIfJA('policy')} will be issued.`,
          `*Please note that your coverage will only begin if payment is received.`,
        ];
      case 'Operations Review:Operations Review':
        const reason = CoverpathUtils.reasonForOR1(this.policy);

        if (reason === ReasonForOR1.CP_CASE) {
          return [`Once ${this.insuredFullName} eSigns, your policy will be issued.`];
        } else if (reason === ReasonForOR1.CP_JOINT_OWNERSHIP) {
          return [
            `Our operations team is working on your application. They'll reach out to you if they need your help.`,
          ];
        } else {
          const phone = this.policy.getChannelInfo().phone;
          return [
            `We're sorry, we're having trouble verifying some information. We'll look into it, but you can also call customer support at ${phone}.`,
          ];
        }
      case 'Funding Pause:Initiate Funds Transfer':
        const coverpathPolicy = this.policy as CoverpathPolicy;
        const offerPreviewSentDate = coverpathPolicy.application.previewOfferSentDate;
        const offerPreviewExpirationDate = moment(Utils.offsetDate(offerPreviewSentDate, 30, 'days')).format(
          'M/D/YYYY'
        );
        return [
          `To proceed, we need your permission to start the 1035 exchange funds transfer process, which is transfer of cash value from your existing life insurance policy into this new MassMutual policy.`,
          `You have until ${offerPreviewExpirationDate} to initiate the 1035 exchange.`,
        ];
      case 'Funding Pause:Waiting for 1035 Funds':
        return [
          `Your policy will be ready when your 1035 exchange funds arrive.`,
          `Your financial professional will contact you when your policy is ready.`,
        ];
      case 'Funding Pause:Funds have arrived': {
        return [`Your financial professional will contact you when your policy is ready.`];
      }
      case 'Conversion started:Conversion started':
        return [`If you’re ready to proceed, the next step is to provide your payment information.`];
      case 'Conversion is Ready:Conversion is Ready':
        return [
          `After your financial professional signs your conversion application, your conversion will take effect.`,
        ];
      default:
        const error = {
          isHumanRelated: false,
          data: {
            workflowAction: this.workflowAction,
          },
        };
        UILoggerSingleton.getInstance().error({
          module: 'BasePolicyStatus',
          function: 'getPolicyStatusSubTitle',
          category: UICategory.request,
          error: error,
        });
        return [];
    }
  }

  async getPolicyStatusText(): Promise<PolicyStatusText> {
    switch (this.workflowAction) {
      case 'Accelerated Application:Waiting for Acceptance':
        return this.constructTextForAcceleratedApplicationWaitingForAcceptance();
      case 'Accelerated Application:Waiting for Signature':
        return this.constructTextForAcceleratedApplicationWaitingForSignature();
      case 'Accelerated Application:Issuing Policy':
        return this.constructTextForAcceleratedApplicationIssuingPolicy();
      case 'In Force:Issued':
        if (this.hasPaymentFailed) {
          return this.constructTextForPaymentFailureInForceIssuedPolicy();
        }
      default:
        return {
          title: this.getPolicyStatusTitle(),
          subtitle: await this.getPolicyStatusSubTitle(),
        };
    }
  }

  private constructTextForAcceleratedApplicationWaitingForAcceptance(): PolicyStatusText {
    return {
      title: [
        `Congratulations!`,
        `You qualify for our express path process.`,
        `Your final premium is ${this.formattedPrice}/${this.timeFrequency}.`,
      ],
      subtitle: [
        `With express path, you get the same pricing without the need for a medical exam. Your final premium is based on your application answers and the data we collect.`,
        `eSign your documents to start your coverage right away.`,
        `This offer expires on ${this.formattedOfferRequiredBy}.`,
      ],
    };
  }

  private constructTextForAcceleratedApplicationWaitingForSignature(): PolicyStatusText {
    const titleText = [];
    const subtitleText = [];
    if (this.waitingOnOtherSigners) {
      // this order is important we show the insured message first even when there are two more parties
      let pronoun: string;
      let email: string;
      if (this.waitingOnInsuredToSign) {
        pronoun = 'insured';
        email = this.insuredEmail;
      } else if (this.waitingOnSecondOwnerToSign) {
        pronoun = 'joint owner';
        email = this.otherOwnerEmail;
      }
      return {
        title: [`Next step: we need the ${pronoun}'s signature`],
        subtitle: [
          `We've emailed the ${pronoun} (${email}) asking for their signature. Once the ${pronoun} signs, we can finish processing your application.`,
        ],
      };
    } else if (this.isBlockedByAdvisor) {
      titleText.push(`Congratulations, your ${this.makePluralIfJA('policy has')} been approved.`);
      subtitleText.push(
        `Once your ${this.advisorDescriptiveNoun} eSigns, your ${this.makePluralIfJA('policy')} will be issued.`
      );
      subtitleText.push(`Please note that your coverage will only begin if payment is received.`);
    } else if (this.signatureNeeded) {
      if (this.controller.isDocuSignRedirectEnabled) {
        titleText.push('Next step: we need your signature.');
      } else {
        titleText.push('Next step: check your email.');
      }
      if (this.isOwnerSameAsInsured) {
        subtitleText.push(`We need you to eSign so your coverage can start immediately.`);
      } else {
        subtitleText.push(
          `We need you and the insured (or the insured's representative) to eSign so your coverage can start immediately.`
        );
      }
    }

    return {
      title: titleText,
      subtitle: subtitleText,
    };
  }

  private constructTextForAcceleratedApplicationIssuingPolicy(): PolicyStatusText {
    return {
      title: [`Congratulations, you're covered!`],
      subtitle: [
        `Your ${this.makePluralIfJA(
          'policy is'
        )} active. We're preparing your policy documents and will email you as soon as they're ready. You can view your policy details below.`,
      ],
    };
  }

  private constructTextForPaymentFailureInForceIssuedPolicy(): PolicyStatusText {
    return {
      title: this.isJointApp
        ? [`Your premium payment failed.`]
        : [`Your ${this.failedPaymentAmount} premium payment failed.`],
      subtitle: ['You need to review your payment information and authorize the payment again.'],
    };
  }
}
